import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { rupiah } from "@/lib/format";
import { PageHeader } from "@/components/app/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CurrencyInput } from "@/components/app/CurrencyInput";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Save, Info, Plus, Trash2 } from "lucide-react";

const STATUS = ["TK/0", "TK/1", "TK/2", "TK/3", "K/0", "K/1", "K/2", "K/3", "K/I/0", "K/I/1", "K/I/2", "K/I/3"];
const METODE = [
  { v: "umkm", l: "UMKM — PPh Final 0,5% (PP 55/2022)" },
  { v: "nppn", l: "NPPN — Norma Penghitungan (neto = omzet × norma%)" },
  { v: "pembukuan", l: "Pembukuan — neto = omzet − biaya (progresif)" },
];

export default function PengaturanPajakPribadi() {
  const [form, setForm] = useState({ status_ptkp: "TK/0", metode_usaha: "umkm", norma_persen: 50, npwp: "", nama_wp: "" });
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savingCfg, setSavingCfg] = useState(false);

  const load = async () => {
    const [s, c] = await Promise.all([api.get("/pajak-pribadi/settings"), api.get("/pajak-config")]);
    setForm((f) => ({ ...f, ...s.data }));
    setCfg(c.data);
  };
  useEffect(() => { load(); }, []);

  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e && e.target ? e.target.value : e }));
  const updCfg = (k) => (e) => setCfg((c) => ({ ...c, [k]: e && e.target ? e.target.value : e }));

  const save = async () => {
    setSaving(true);
    try { await api.put("/pajak-pribadi/settings", { ...form, norma_persen: Number(form.norma_persen) || 0 }); toast.success("Pengaturan tersimpan"); }
    catch (e) { toast.error(formatApiError(e)); } finally { setSaving(false); }
  };

  const saveCfg = async () => {
    setSavingCfg(true);
    try {
      const payload = {
        ptkp_diri: Number(cfg.ptkp_diri) || 0, ptkp_kawin: Number(cfg.ptkp_kawin) || 0,
        ptkp_tanggungan: Number(cfg.ptkp_tanggungan) || 0, ptkp_max_tanggungan: Number(cfg.ptkp_max_tanggungan) || 3,
        biaya_jabatan_rate: Number(cfg.biaya_jabatan_rate) || 0, biaya_jabatan_max: Number(cfg.biaya_jabatan_max) || 0,
        umkm_rate: Number(cfg.umkm_rate) || 0, umkm_batas_bebas: Number(cfg.umkm_batas_bebas) || 0, umkm_omzet_max: Number(cfg.umkm_omzet_max) || 0,
        brackets: (cfg.brackets || []).map((b) => ({ batas: b.batas === null || b.batas === "" ? null : Number(b.batas), tarif_persen: Number(b.tarif_persen) || 0 })),
        final_presets: (cfg.final_presets || []).map((p) => ({ label: p.label, tarif_persen: Number(p.tarif_persen) || 0 })),
      };
      await api.put("/pajak-config", payload); toast.success("Konfigurasi pajak tersimpan");
    } catch (e) { toast.error(formatApiError(e)); } finally { setSavingCfg(false); }
  };

  const setBracket = (i, k, v) => setCfg((c) => { const b = [...c.brackets]; b[i] = { ...b[i], [k]: v }; return { ...c, brackets: b }; });
  const addBracket = () => setCfg((c) => ({ ...c, brackets: [...(c.brackets || []), { batas: 0, tarif_persen: 0 }] }));
  const delBracket = (i) => setCfg((c) => ({ ...c, brackets: c.brackets.filter((_, j) => j !== i) }));
  const setPreset = (i, k, v) => setCfg((c) => { const p = [...c.final_presets]; p[i] = { ...p[i], [k]: v }; return { ...c, final_presets: p }; });
  const addPreset = () => setCfg((c) => ({ ...c, final_presets: [...(c.final_presets || []), { label: "", tarif_persen: 0 }] }));
  const delPreset = (i) => setCfg((c) => ({ ...c, final_presets: c.final_presets.filter((_, j) => j !== i) }));

  return (
    <>
      <PageHeader title="Pengaturan Pajak Pribadi" subtitle="Status PTKP, metode usaha, & tarif pajak dinamis (bisa diubah bila regulasi berubah)." actions={
        <Button onClick={save} disabled={saving} className="brand-btn rounded-sm" data-enter-submit data-testid="pajak-save"><Save size={14} className="mr-1" /> {saving ? "Menyimpan..." : "Simpan Profil"}</Button>
      } />
      <div className="p-4 md:p-8 space-y-6 max-w-4xl">
        <div className="card-flat p-6 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="space-y-1.5"><Label>Nama Wajib Pajak</Label><Input value={form.nama_wp || ""} onChange={upd("nama_wp")} data-testid="pajak-nama" /></div>
          <div className="space-y-1.5"><Label>NPWP</Label><Input value={form.npwp || ""} onChange={upd("npwp")} placeholder="00.000.000.0-000.000" data-testid="pajak-npwp" /></div>
          <div className="space-y-1.5"><Label>Status PTKP</Label>
            <Select value={form.status_ptkp} onValueChange={(v) => setForm((f) => ({ ...f, status_ptkp: v }))}>
              <SelectTrigger data-testid="pajak-ptkp"><SelectValue /></SelectTrigger>
              <SelectContent>{STATUS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5"><Label>Metode Penghitungan Usaha</Label>
            <Select value={form.metode_usaha} onValueChange={(v) => setForm((f) => ({ ...f, metode_usaha: v }))}>
              <SelectTrigger data-testid="pajak-metode"><SelectValue /></SelectTrigger>
              <SelectContent>{METODE.map((m) => <SelectItem key={m.v} value={m.v}>{m.l}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {form.metode_usaha === "nppn" && (
            <div className="space-y-1.5"><Label>Persentase Norma (%)</Label><Input type="number" value={form.norma_persen} onChange={upd("norma_persen")} data-testid="pajak-norma" /></div>
          )}
        </div>

        {cfg && (
          <div className="card-flat p-6 space-y-5" data-enter-scope>
            <div className="flex items-center justify-between">
              <div className="text-sm font-semibold">Tarif Pajak Dinamis (UU HPP) — hanya Admin</div>
              <Button onClick={saveCfg} disabled={savingCfg} variant="outline" className="rounded-sm" data-enter-submit data-testid="cfg-save"><Save size={14} className="mr-1" /> {savingCfg ? "Menyimpan..." : "Simpan Tarif"}</Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-1.5"><Label>PTKP Diri</Label><CurrencyInput value={cfg.ptkp_diri} onValueChange={updCfg("ptkp_diri")} /></div>
              <div className="space-y-1.5"><Label>PTKP Tambahan Kawin</Label><CurrencyInput value={cfg.ptkp_kawin} onValueChange={updCfg("ptkp_kawin")} /></div>
              <div className="space-y-1.5"><Label>PTKP per Tanggungan</Label><CurrencyInput value={cfg.ptkp_tanggungan} onValueChange={updCfg("ptkp_tanggungan")} /></div>
              <div className="space-y-1.5"><Label>Biaya Jabatan (%)</Label><Input type="number" value={cfg.biaya_jabatan_rate} onChange={updCfg("biaya_jabatan_rate")} /></div>
              <div className="space-y-1.5"><Label>Biaya Jabatan Maks/th</Label><CurrencyInput value={cfg.biaya_jabatan_max} onValueChange={updCfg("biaya_jabatan_max")} /></div>
              <div className="space-y-1.5"><Label>Tarif UMKM Final (%)</Label><Input type="number" step="0.1" value={cfg.umkm_rate} onChange={updCfg("umkm_rate")} /></div>
              <div className="space-y-1.5"><Label>UMKM Batas Bebas Pajak</Label><CurrencyInput value={cfg.umkm_batas_bebas} onValueChange={updCfg("umkm_batas_bebas")} /></div>
              <div className="space-y-1.5"><Label>UMKM Batas Omzet Maks</Label><CurrencyInput value={cfg.umkm_omzet_max} onValueChange={updCfg("umkm_omzet_max")} /></div>
            </div>

            <div>
              <div className="flex items-center justify-between mb-2"><Label>Lapisan Tarif Progresif (Pasal 17)</Label><Button size="sm" variant="outline" className="rounded-sm h-7" onClick={addBracket}><Plus size={12} className="mr-1" /> Lapisan</Button></div>
              <div className="text-[11px] text-slate-500 mb-1">Kosongkan "Batas Atas" pada lapisan terakhir untuk "tak terhingga".</div>
              {(cfg.brackets || []).map((b, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input type="number" placeholder="Batas atas (Rp)" value={b.batas ?? ""} onChange={(e) => setBracket(i, "batas", e.target.value === "" ? null : e.target.value)} />
                  <Input type="number" className="w-28" placeholder="Tarif %" value={b.tarif_persen} onChange={(e) => setBracket(i, "tarif_persen", e.target.value)} />
                  <button onClick={() => delBracket(i)} className="p-2 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-600" /></button>
                </div>
              ))}
            </div>

            <div>
              <div className="flex items-center justify-between mb-2"><Label>Preset Tarif PPh Final</Label><Button size="sm" variant="outline" className="rounded-sm h-7" onClick={addPreset}><Plus size={12} className="mr-1" /> Preset</Button></div>
              {(cfg.final_presets || []).map((p, i) => (
                <div key={i} className="flex items-center gap-2 mb-2">
                  <Input placeholder="Label (mis. Dividen 10%)" value={p.label} onChange={(e) => setPreset(i, "label", e.target.value)} />
                  <Input type="number" step="0.1" className="w-28" placeholder="Tarif %" value={p.tarif_persen} onChange={(e) => setPreset(i, "tarif_persen", e.target.value)} />
                  <button onClick={() => delPreset(i)} className="p-2 rounded-sm hover:bg-rose-50"><Trash2 size={14} className="text-rose-600" /></button>
                </div>
              ))}
            </div>
          </div>
        )}

        <div className="card-flat p-4 flex gap-3 border-l-4 border-l-blue-500">
          <Info size={18} className="text-blue-600 shrink-0" />
          <div className="text-sm text-slate-600 space-y-1">
            <p><b>UMKM 0,5%</b>: omzet usaha Rp 500.000.000 pertama per tahun BEBAS PAJAK; 0,5% hanya atas kelebihannya (PP 55/2022).</p>
            <p>Nilai default sudah sesuai <b>UU HPP</b>. Ubah di sini bila regulasi berubah — tidak perlu ubah kode.</p>
          </div>
        </div>
      </div>
    </>
  );
}
